/**
 * R2Service
 *
 * Server-side utility for reading from / writing to a Cloudflare R2 bucket
 * via its S3-compatible API. Counterpart to `StorageUrlService` (Supabase).
 *
 * ⚠️  ARCHITECTURE LOCK (2026-05-12) — R2 is the source-of-truth for all
 * Ballee media (photos, headshots, documents, video originals). Do NOT
 * propose moving to a different object store without re-running the
 * deep research framework in:
 *   docs/architecture/video-service-decision-framework-2026-05-10.md
 * and updating the Critical Rule clause:
 *   .claude/clauses/critical-rules-video-architecture.md
 *
 * Why R2 specifically: $0 egress is the highest-impact decision for a
 * media-heavy SaaS. Even if delivery vendor changes (Mux, Bunny, etc.),
 * R2-as-origin keeps options open.
 *
 * SINGLE-EDIT-SURFACE rule: ALL R2 access in the codebase must go through
 * this service. Direct `@aws-sdk/client-s3` imports elsewhere are forbidden
 * (anti-pattern flagged in critical-rules-video-architecture.md).
 *
 * Public R2 content is served via the Cloudflare-managed custom domain
 * `cdn.ballee.co` (set via `R2_PUBLIC_URL`). Reads from a browser do NOT
 * go through this service — they hit the public URL directly.
 *
 * This service is for:
 *   - Server-side uploads (migration scripts, route handlers that proxy uploads)
 *   - Object existence checks
 *   - Listing (admin / cleanup operations)
 *   - Building presigned URLs when private R2 buckets are needed (rare;
 *     prefer Supabase + RLS for private content)
 *
 * NEVER import this file from a client component. The SDK is heavy and the
 * credentials must never reach the browser.
 *
 * @see https://developers.cloudflare.com/r2/api/s3/api/
 * @see .claude/clauses/critical-rules-video-architecture.md
 * @see docs/architecture/video-service-decision-framework-2026-05-10.md
 */
import {
  GetObjectCommand,
  HeadObjectCommand,
  ListObjectsV2Command,
  PutObjectCommand,
  S3Client,
} from '@aws-sdk/client-s3';

import { Result, ServiceError } from '@kit/shared/result';

/**
 * R2 bucket identifiers used in the application.
 *
 * IMPORTANT: Always use these constants instead of hardcoded strings — same
 * convention as `StorageBuckets`.
 */
export const R2Buckets = {
  /** New canonical bucket for migrated public media (post-Meteor cutover). */
  MEDIA_PROD: 'ballee-media-prod',
  /** Existing bucket created by the Meteor app (avatars / headers under
   *  `IMAGE_MODULES/<userId>/<file>`). Read-only from the new app. */
  ASSETS_LEGACY: 'ballee-assets',
} as const;

export type R2Bucket = (typeof R2Buckets)[keyof typeof R2Buckets];

export interface R2ServiceConfig {
  /** R2 endpoint, e.g. `https://<account>.r2.cloudflarestorage.com` */
  endpoint: string;
  /** R2 access key (from Cloudflare R2 dashboard) */
  accessKeyId: string;
  /** R2 secret access key */
  secretAccessKey: string;
  /** Public-facing URL prefix (e.g. `https://media.ballee.app`) */
  publicUrl: string;
}

export interface R2UploadOptions {
  /** Override the default bucket (defaults to `R2Buckets.MEDIA_PROD`) */
  bucket?: R2Bucket;
  /** Content-Type. Required by R2 to render correctly in browsers. */
  contentType: string;
  /** Cache-Control header. Defaults to `public, max-age=31536000, immutable`. */
  cacheControl?: string;
  /** Whether to overwrite if the object already exists (default true). */
  upsert?: boolean;
}

export interface R2ListResult {
  key: string;
  size: number;
  lastModified: Date | null;
}

/**
 * Build an R2Service from the canonical env vars.
 *
 * Required env (server only):
 *   R2_ENDPOINT, R2_ACCESS_KEY_ID, R2_SECRET_ACCESS_KEY, R2_PUBLIC_URL
 *
 * @example
 * ```typescript
 * const r2 = createR2ServiceFromEnv();
 * await r2.upload('events/abc/header.jpg', buffer, {
 *   contentType: 'image/jpeg',
 * });
 * const url = r2.getPublicUrl('events/abc/header.jpg');
 * ```
 */
export function createR2ServiceFromEnv(): R2Service {
  const endpoint = process.env.R2_ENDPOINT;
  const accessKeyId = process.env.R2_ACCESS_KEY_ID;
  const secretAccessKey = process.env.R2_SECRET_ACCESS_KEY;
  const publicUrl = process.env.R2_PUBLIC_URL;
  if (!endpoint || !accessKeyId || !secretAccessKey || !publicUrl) {
    throw new Error(
      'R2 env not configured. Required: R2_ENDPOINT, R2_ACCESS_KEY_ID, R2_SECRET_ACCESS_KEY, R2_PUBLIC_URL',
    );
  }
  return new R2Service({ endpoint, accessKeyId, secretAccessKey, publicUrl });
}

export class R2Service {
  private client: S3Client;
  private publicUrl: string;

  constructor(config: R2ServiceConfig) {
    this.client = new S3Client({
      region: 'auto',
      endpoint: config.endpoint,
      credentials: {
        accessKeyId: config.accessKeyId,
        secretAccessKey: config.secretAccessKey,
      },
    });
    this.publicUrl = config.publicUrl.replace(/\/+$/, '');
  }

  /**
   * Public URL for a key on the canonical bucket. Returns
   * `${R2_PUBLIC_URL}/<key>` — does NOT validate that the object exists.
   */
  getPublicUrl(key: string): string {
    if (!key) return this.publicUrl;
    return `${this.publicUrl}/${key.replace(/^\/+/, '')}`;
  }

  /**
   * Upload a Buffer or Uint8Array to R2.
   *
   * @example
   * ```typescript
   * await r2.upload('community-events/abc/header.jpg', imageBuffer, {
   *   contentType: 'image/jpeg',
   * });
   * ```
   */
  async upload(
    key: string,
    body: Buffer | Uint8Array,
    options: R2UploadOptions,
  ): Promise<Result<{ key: string; bucket: R2Bucket }>> {
    const bucket = options.bucket ?? R2Buckets.MEDIA_PROD;
    try {
      await this.client.send(
        new PutObjectCommand({
          Bucket: bucket,
          Key: key,
          Body: body,
          ContentType: options.contentType,
          CacheControl:
            options.cacheControl ?? 'public, max-age=31536000, immutable',
        }),
      );
      return Result.ok({ key, bucket });
    } catch (err) {
      return Result.fail(
        ServiceError.internal(
          err instanceof Error ? err.message : 'R2 upload failed',
          err,
        ),
      );
    }
  }

  /**
   * Check whether an object exists in the bucket. Cheap HEAD request.
   */
  async exists(
    key: string,
    bucket: R2Bucket = R2Buckets.MEDIA_PROD,
  ): Promise<boolean> {
    try {
      await this.client.send(
        new HeadObjectCommand({ Bucket: bucket, Key: key }),
      );
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Read an object's body. Use sparingly — prefer the public URL for
   * browser-facing reads.
   */
  async get(
    key: string,
    bucket: R2Bucket = R2Buckets.MEDIA_PROD,
  ): Promise<Result<Buffer>> {
    try {
      const res = await this.client.send(
        new GetObjectCommand({ Bucket: bucket, Key: key }),
      );
      const arr = await res.Body?.transformToByteArray();
      if (!arr) {
        return Result.fail(ServiceError.internal('R2 GET returned empty body'));
      }
      return Result.ok(Buffer.from(arr));
    } catch (err) {
      return Result.fail(
        ServiceError.internal(
          err instanceof Error ? err.message : 'R2 GET failed',
          err,
        ),
      );
    }
  }

  /**
   * Read the first `length` bytes of an object via an HTTP Range request.
   *
   * Exists for header-only reads (see `image-dimensions.ts`): stamping the
   * intrinsic size of 7.5k legacy headshots needs a few KB per object, not the
   * multi-megabyte original. Returns fewer bytes than asked when the object is
   * smaller — that is a normal short read, not an error.
   */
  async getHeadBytes(
    key: string,
    length: number,
    bucket: R2Bucket = R2Buckets.MEDIA_PROD,
  ): Promise<Result<Buffer>> {
    if (length <= 0) {
      return Result.fail(
        ServiceError.validation('Range length must be positive'),
      );
    }
    try {
      const res = await this.client.send(
        new GetObjectCommand({
          Bucket: bucket,
          Key: key,
          Range: `bytes=0-${length - 1}`,
        }),
      );
      const arr = await res.Body?.transformToByteArray();
      if (!arr) {
        return Result.fail(
          ServiceError.internal('R2 ranged GET returned empty body'),
        );
      }
      return Result.ok(Buffer.from(arr));
    } catch (err) {
      return Result.fail(
        ServiceError.internal(
          err instanceof Error ? err.message : 'R2 ranged GET failed',
          err,
        ),
      );
    }
  }

  /**
   * Stream every object in a bucket (paginated).
   *
   * @example
   * ```typescript
   * for await (const obj of r2.listAll(R2Buckets.MEDIA_PROD)) {
   *   console.log(obj.key, obj.size);
   * }
   * ```
   */
  async *listAll(
    bucket: R2Bucket = R2Buckets.MEDIA_PROD,
    prefix?: string,
  ): AsyncIterable<R2ListResult> {
    let continuationToken: string | undefined;
    do {
      const res = await this.client.send(
        new ListObjectsV2Command({
          Bucket: bucket,
          ContinuationToken: continuationToken,
          Prefix: prefix,
          MaxKeys: 1000,
        }),
      );
      for (const obj of res.Contents ?? []) {
        if (!obj.Key) continue;
        yield {
          key: obj.Key,
          size: obj.Size ?? 0,
          lastModified: obj.LastModified ?? null,
        };
      }
      continuationToken = res.NextContinuationToken;
    } while (continuationToken);
  }
}
